import React, { useState, useRef, useEffect } from 'react'
import { Send, Mic, MicOff, RotateCcw, Sparkles, Zap, Clock, Users } from 'lucide-react'
import { eventService } from '../../services/eventService'
import type { ChatMessage, Suggestion, Event } from '../../types/mockup'

interface ChatInterfaceProps {
  event: Event
  onCommandExecute?: (command: string, result: { success: boolean; message?: string }) => void
  onSuggestionCreate?: (suggestion: Suggestion) => void
  isProcessing?: boolean
}

export const ChatInterface: React.FC<ChatInterfaceProps> = ({
  event,
  onCommandExecute,
  onSuggestionCreate,
  isProcessing = false
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 'chat-1',
      type: 'user',
      content: 'Move cocktail hour back by 15 minutes',
      timestamp: new Date(new Date().setMinutes(new Date().getMinutes() - 30)),
      sender: 'Event Coordinator'
    },
    {
      id: 'chat-2',
      type: 'system',
      content: 'Cocktail hour has been rescheduled to 4:45 PM. All dependent tasks have been adjusted. Notification sent to catering team.',
      timestamp: new Date(new Date().setMinutes(new Date().getMinutes() - 29)),
      sender: 'Clokd'
    },
    {
      id: 'chat-3',
      type: 'user',
      content: 'Show me the current status of all vendors',
      timestamp: new Date(new Date().setMinutes(new Date().getMinutes() - 15)),
      sender: 'Event Coordinator'
    },
    {
      id: 'chat-4',
      type: 'system',
      content: 'All vendors are on-site and ready:\n• Catering: On-site (Kitchen)\n• Florist: Complete\n• Photographer: Active (Bridal Suite)\n• Band: Ready\n• AV Team: Complete\n• Venue Staff: Active',
      timestamp: new Date(new Date().setMinutes(new Date().getMinutes() - 14)),
      sender: 'Clokd'
    }
  ])
  const [inputValue, setInputValue] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [commandHistory, setCommandHistory] = useState<string[]>([])
  const [historyIndex, setHistoryIndex] = useState(-1)
  const [isVoiceEnabled, setIsVoiceEnabled] = useState(false)
  
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const chatContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const addMessage = (message: Omit<ChatMessage, 'id' | 'timestamp'>) => {
    const newMessage: ChatMessage = {
      ...message,
      id: `msg-${Date.now()}-${Math.random()}`,
      timestamp: new Date()
    }
    setMessages(prev => [...prev, newMessage])
    return newMessage
  }

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isProcessing) return

    const userMessage = inputValue.trim()
    setInputValue('')
    setHistoryIndex(-1)
    
    // Add to command history
    setCommandHistory(prev => [...prev, userMessage])
    
    // Add user message
    addMessage({
      type: 'user',
      content: userMessage,
      sender: 'Event Coordinator',
      avatar: 'EC'
    })

    // Show typing indicator
    setIsTyping(true)

    // Process command through backend
    processCommand(userMessage)
  }

  const processCommand = async (command: string) => {
    try {
      // Call backend API for command processing
      const response = await eventService.processCommand(event.id, command)
      
      setIsTyping(false)
      
      // Handle the response based on type
      if (response.chatResponse) {
        // Add the AI response message
        addMessage({
          type: response.chatResponse.type as 'system' | 'suggestion',
          content: response.chatResponse.content,
          sender: response.chatResponse.sender || 'Clokd AI',
          suggestion: response.suggestion
        })
        
        // If there's a suggestion, notify parent
        if (response.suggestion) {
          onSuggestionCreate?.(response.suggestion)
        }
      }
      
      // Notify parent of command execution
      onCommandExecute?.(command, { 
        success: response.success, 
        message: response.message 
      })
      
    } catch (error) {
      setIsTyping(false)
      
      // Fallback to local processing for common queries
      const lowerCommand = command.toLowerCase()
      
      // Handle queries locally with rich formatting as fallback
      if (lowerCommand.includes('help')) {
        addMessage({
          type: 'system',
          content: `**🚀 Available Commands:**

**Schedule Management:**
• \`delay [task] by [X] minutes\` - Reschedule tasks
• \`what's happening now?\` - Current status
• \`mark [task] as complete\` - Update task status

**Team Coordination:**
• \`where is the [supplier]?\` - Find team members
• \`notify [supplier] about [message]\` - Send updates
• \`call [supplier]\` - Initiate contact

**Event Intelligence:**
• \`guest count\` - Attendance updates  
• \`weather update\` - Current conditions
• \`photo status\` - Photography progress

💡 **Pro tip:** Use natural language! I understand context and can help with complex requests.`,
          sender: 'Clokd Ops'
        })
      } else if (lowerCommand.includes('weather')) {
        addMessage({
          type: 'system',
          content: `☁️ **Weather Update:**\n\n🌡️ Currently 72°F, partly cloudy\n🌧️ 15% chance of rain this evening\n🌬️ Light breeze 8mph\n\n✅ Perfect conditions for the outdoor cocktail hour!`,
          sender: 'Clokd AI'
        })
      } else if (lowerCommand.includes('guest')) {
        addMessage({
          type: 'system',
          content: `👥 **Guest Status Update:**\n\n✅ **Confirmed:** 147 guests\n💬 **RSVP Pending:** 3 guests\n🍽️ **Dietary Requirements:** 12 vegetarian, 4 vegan, 3 gluten-free`,
          sender: 'Clokd AI'
        })
      } else {
        // Generic error message
        addMessage({
          type: 'system',
          content: 'I encountered an issue processing your command. Please try again or type "help" for available commands.',
          sender: 'Clokd AI'
        })
      }
      
      onCommandExecute?.(command, { success: false })
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    } else if (e.key === 'ArrowUp' && commandHistory.length > 0) {
      e.preventDefault()
      const newIndex = historyIndex < commandHistory.length - 1 ? historyIndex + 1 : historyIndex
      setHistoryIndex(newIndex)
      setInputValue(commandHistory[commandHistory.length - 1 - newIndex])
    } else if (e.key === 'ArrowDown' && historyIndex > -1) {
      e.preventDefault()
      const newIndex = historyIndex - 1
      setHistoryIndex(newIndex)
      setInputValue(newIndex >= 0 ? commandHistory[commandHistory.length - 1 - newIndex] : '')
    }
  }

  const handleVoiceClick = () => {
    setIsVoiceEnabled(!isVoiceEnabled)
    if (!isVoiceEnabled) {
      // Simulated voice input
      setTimeout(() => {
        setInputValue('Delay ceremony by 15 minutes')
        setIsVoiceEnabled(false)
      }, 2000)
    }
  }

  const getSupplierLocation = (supplier: Supplier) => {
    const locationMap = {
      'Florist': {
        status: '🌸 **Bloom & Blossom Florals** is on-site in the Main Ballroom',
        brief: 'Main Ballroom (setting up centerpieces)',
        action: '💡 They requested clarification on table arrangements. Should I send them the updated floor plan?'
      },
      'Photographer': {
        status: '📸 **Capture Moments Photography** is currently in the Bridal Suite',
        brief: 'Bridal Suite (portraits session)',
        action: '📅 Next scheduled: Family portraits at 3:30 PM in the Garden Terrace'
      },
      'Catering': {
        status: '🍽️ **Elite Catering Co.** is active in the Kitchen prep area',
        brief: 'Kitchen (final prep)',
        action: '⏰ Running 10 minutes behind due to traffic. Cocktail setup moved to 4:45 PM'
      },
      'Band/Music': {
        status: '🎵 **The Jazz Quartet** is on-site doing sound check in Main Ballroom',
        brief: 'Main Ballroom (sound check)',
        action: '🔊 Sound check complete. Ready for ceremony at 5:00 PM'
      },
      'Decorator': {
        status: '🎨 **Elegant Events Decor** is in the Reception Hall finalizing setup',
        brief: 'Reception Hall (final touches)',
        action: '✨ 95% complete. Lighting adjustments ongoing'
      },
      'Audio/Visual': {
        status: '🎤 **Premier AV Solutions** has completed setup in both venues',
        brief: 'Both venues (monitoring)',
        action: '✅ All systems operational. Monitoring from control booth'
      }
    }
    
    return locationMap[supplier.role] || {
      status: `${supplier.name} is ${supplier.status === 'on_site' ? 'on-site and active' : 'en route'}`,
      brief: supplier.status === 'on_site' ? 'On-site' : 'En route',
      action: 'Would you like me to contact them for an update?'
    }
  }

  const handleSuggestionAction = (suggestion: Suggestion, action: 'approve' | 'edit' | 'ignore') => {
    // Update the suggestion status in messages
    setMessages(prev => prev.map(msg => {
      if (msg.suggestion?.id === suggestion.id) {
        return {
          ...msg,
          suggestion: {
            ...msg.suggestion,
            status: action === 'approve' ? 'approved' : action === 'edit' ? 'edited' : 'ignored',
            actionTaken: action,
            actionBy: 'Event Coordinator',
            actionAt: new Date()
          }
        }
      }
      return msg
    }))

    // Show typing indicator
    setIsTyping(true)

    // Simulate processing delay
    setTimeout(() => {
      setIsTyping(false)
      
      if (action === 'approve') {
        // Simulate schedule update
        const affectedTasks = suggestion.impact.tasksAffected.length
        const minutesDelay = suggestion.impact.minutesImpact || 15
        
        addMessage({
          type: 'system',
          content: `✅ **Schedule Change Applied**\n\n🕒 ${affectedTasks} tasks delayed by ${minutesDelay} minutes\n📋 Updated timeline:\n• Ceremony: 5:15 PM → 5:30 PM\n• Cocktail Hour: 6:30 PM → 6:45 PM\n• Reception: 7:30 PM → 7:45 PM\n\n📤 Notifications sent to:\n• Elite Catering Co.\n• Bloom & Blossom Florals\n• Capture Moments Photography\n\n⚡ All dependencies automatically adjusted`,
          sender: 'Clokd AI'
        })
        
        // Show vendor confirmations
        setTimeout(() => {
          addMessage({
            type: 'notification',
            content: `📱 **Vendor Confirmations:**\n\n✅ Elite Catering: "Confirmed. Adjusting prep schedule."\n✅ Bloom & Blossom: "No problem. Flowers will stay fresh."\n⏳ Capture Moments: "Acknowledged. Adjusting shot list."\n\n🎯 All vendors have confirmed the new timeline.`,
            sender: 'System Updates'
          })
        }, 2000)
        
      } else if (action === 'ignore') {
        addMessage({
          type: 'system',
          content: `🚫 **Suggestion Ignored**\n\n📝 Schedule change request dismissed\n⏰ Original timeline maintained\n📊 No tasks affected\n\n💡 You can always request schedule changes later if needed.`,
          sender: 'Clokd AI'
        })
        
      } else if (action === 'edit') {
        addMessage({
          type: 'system',
          content: `✏️ **Suggestion Modified**\n\n🔄 Opening advanced scheduler...\n\n💭 Popular modifications:\n• Change delay to 10 minutes instead\n• Delay only ceremony, keep reception time\n• Add buffer time between events\n\n📝 What adjustment would you like to make?`,
          sender: 'Clokd AI'
        })
      }
    }, 1000 + Math.random() * 1000)
  }

  const formatTimestamp = (date: Date): string => {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  }

  return (
    <div className="chat-interface">
      <div className="chat-header">
        <h3>Command Center</h3>
        <div className="chat-header-actions">
          <button 
            className="clear-chat-btn"
            onClick={() => setMessages([messages[0]])}
            title="Clear chat"
          >
            🗑️
          </button>
        </div>
      </div>

      <div className="chat-messages" ref={chatContainerRef}>
        {messages.map((message) => (
          <div 
            key={message.id} 
            className={`chat-message ${message.type}-message`}
          >
            <div className="message-avatar">
              {message.type === 'user' ? (
                <div className="user-avatar">
                  <span className="avatar-text">EC</span>
                  <div className="avatar-name">Event Coordinator</div>
                </div>
              ) : (
                <div className="system-avatar">
                  <span className="avatar-logo">🤖</span>
                  <div className="avatar-name">Clokd AI</div>
                </div>
              )}
            </div>
            <div className="message-content">
              <div className="message-text">{message.content}</div>
              {message.suggestion && (
                <div className={`embedded-suggestion ${message.suggestion.status || 'pending'}`}>
                  <div className="suggestion-header">
                    <span className="suggestion-type">{message.suggestion.type.replace('_', ' ')}</span>
                    {message.suggestion.status && message.suggestion.status !== 'pending' && (
                      <div className={`suggestion-status-overlay ${message.suggestion.status}`}>
                        {message.suggestion.status === 'approved' && '✅ Approved'}
                        {message.suggestion.status === 'ignored' && '🚫 Ignored'}
                        {message.suggestion.status === 'edited' && '✏️ Edited'}
                      </div>
                    )}
                  </div>
                  <div className="suggestion-description">
                    {message.suggestion.description}
                  </div>
                  <div className="suggestion-impact">
                    {message.suggestion.impact.cascadingChanges?.map((change, i) => {
                      if (change.startsWith('HEADER:')) {
                        const content = change.replace('HEADER:', '')
                        return (
                          <div key={i} className="impact-header" dangerouslySetInnerHTML={{ 
                            __html: content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                          }}></div>
                        )
                      } else if (change.startsWith('TASK:')) {
                        const parts = change.replace('TASK:', '').split(':')
                        const number = parts[0]
                        const name = parts[1].replace(/\*\*(.*?)\*\*/g, '$1')
                        const time = parts[2]
                        const supplier = parts[3]
                        return (
                          <div key={i} className="impact-task-box">
                            <div className="task-number">{number}</div>
                            <div className="task-details">
                              <div className="task-name">{name}</div>
                              <div className="task-meta">{time} {supplier}</div>
                            </div>
                          </div>
                        )
                      } else if (change.startsWith('SUPPLIER:')) {
                        const content = change.replace('SUPPLIER:', '')
                        return (
                          <div key={i} className="impact-supplier">{content}</div>
                        )
                      } else if (change.startsWith('SPACER:')) {
                        return <div key={i} className="impact-spacer"></div>
                      } else if (change.startsWith('INFO:')) {
                        const content = change.replace('INFO:', '')
                        return (
                          <div key={i} className="impact-info">{content}</div>
                        )
                      }
                      return null
                    })}
                  </div>
                  {(!message.suggestion.status || message.suggestion.status === 'pending') && (
                    <div className="suggestion-actions">
                      <button 
                        className="action-approve" 
                        onClick={() => handleSuggestionAction(message.suggestion!, 'approve')}
                      >
                        Approve
                      </button>
                      <button 
                        className="action-edit" 
                        onClick={() => handleSuggestionAction(message.suggestion!, 'edit')}
                      >
                        Edit
                      </button>
                      <button 
                        className="action-ignore" 
                        onClick={() => handleSuggestionAction(message.suggestion!, 'ignore')}
                      >
                        Ignore
                      </button>
                    </div>
                  )}
                </div>
              )}
              <div className="message-timestamp">{formatTimestamp(message.timestamp)}</div>
            </div>
          </div>
        ))}
        
        {isTyping && (
          <div className="chat-message system-message">
            <div className="message-content">
              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input-container">
        <div className="chat-input-wrapper">
          <input
            ref={inputRef}
            type="text"
            className="chat-input"
            placeholder="Type a command... (e.g., 'Delay ceremony by 10 minutes')"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isProcessing}
          />
          <button
            className={`voice-input-btn ${isVoiceEnabled ? 'active' : ''}`}
            onClick={handleVoiceClick}
            title="Voice input"
          >
            {isVoiceEnabled ? '🔴' : '🎤'}
          </button>
          <button
            className="send-btn"
            onClick={handleSendMessage}
            disabled={!inputValue.trim() || isProcessing}
          >
            Send
          </button>
        </div>
        <div className="input-hint">
          Press ↑/↓ for command history • Type "help" for available commands
        </div>
      </div>
    </div>
  )
}

export default ChatInterface